import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
// Material-UI imports
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { isTestEnvironment } from './utils/apiConfig';
import React, { useEffect, useState, useCallback } from "react";

// MSAL imports
import { MsalProvider, useIsAuthenticated, useMsal } from "@azure/msal-react";
import { CustomNavigationClient } from "./utils/NavigationClient";

// Axios auth interceptor — adds Bearer tokens to AI Engine requests
import './utils/axiosAuth';

// Sample app imports
import AppShell from "./ui-components/AppShell";
import { Home } from "./pages/Home";
import DevLogin from "./pages/DevLogin/DevLogin";
import PortfolioPagina from "./pages/Portfolio/PortfolioPagina";
import ExternDownload from "./pages/Bestanden/ExternDownload";
import { getDevSession } from "./utils/devSession";
import {MaterialReservation} from './pages/MaterialReservation/MaterialReservation'
import { SRTomzettenFCP } from "./pages/SRTomzettenFCP/SRTomzettenFCP";
import SRTGenerator from "./pages/SRTGenerator/SRTGenerator";
import SRTDashboard from "./pages/SRTDashboard/SRTDashboard";
import Ondertiteling from "./pages/Ondertiteling/Ondertiteling";
import BatchTranslate from "./pages/SRTGenerator/BatchTranslate";
import BatchTranscribe from "./pages/SRTGenerator/BatchTranscribe";
import MeetingDashboard from "./pages/MeetingDashboard/MeetingDashboard";
import MeetingDetailPage from "./pages/MeetingDashboard/MeetingDetailPage";
import ArchiveViewer from "./pages/Archive/ArchiveViewer";
import ProjectOverview from "./pages/ProjectOverview/ProjectOverview";
import ProjectList from "./pages/ProjectOverview/ProjectList";
import RoleDashboard from "./pages/RoleDashboard/RoleDashboard";
import CallSheetList from "./pages/CallSheet/CallSheetList";
import CallSheetManager from "./pages/CallSheet/CallSheetManager";
import CallSheetBriefing from "./pages/CallSheet/CallSheetBriefing";
import CreateTask from "./pages/Tasks/CreateTask";
import CreateDeliverable from "./pages/Deliverables/CreateDeliverable";
import Dagstart from "./pages/Dagstart/Dagstart";
import DraaidagenOverzicht from "./pages/DraaidagenOverzicht/DraaidagenOverzicht";
import OverzichtPerProject from "./pages/PlanningOverzicht/OverzichtPerProject";
import WeekoverzichtPL from "./pages/PlanningOverzicht/WeekoverzichtPL";
import WeekPlan from "./pages/WeekPlan/WeekPlan";
import MijnTaken from "./pages/MijnTaken/MijnTaken";
import MijnTodos from "./pages/MijnTodos/MijnTodos";
import AanvragenPortaal from "./pages/AanvragenPortaal/AanvragenPortaal";
import Contentkalender from "./pages/Contentkalender/Contentkalender";
import Overdracht from "./pages/Overdracht/Overdracht";
import LeveranciersBeoordelingen from "./pages/LeveranciersBeoordelingen/LeveranciersBeoordelingen";
import Medewerkers from "./pages/Medewerkers/Medewerkers";
import HrDashboard from "./pages/Medewerkers/HrDashboard";
import Leveranciers from "./pages/Leveranciers/Leveranciers";
import SalesDashboard from "./pages/SalesDashboard/SalesDashboard";
import RevenueForecast from "./pages/RevenueForecast/RevenueForecast";
import BbiDoel from "./pages/BbiDoel/BbiDoel";
import Declarabiliteit from "./pages/Declarabiliteit/Declarabiliteit";
import DeclarabiliteitPersoon from "./pages/Declarabiliteit/DeclarabiliteitPersoon";
import VideoDownloader from "./pages/VideoDownloader/VideoDownloader";
import ColorConverter from "./pages/ColorConverter/ColorConverter";
import QRGenerator from "./pages/QRGenerator/QRGenerator";
import NameTitleGenerator from "./pages/NameTitleGenerator/NameTitleGenerator";
import FormaatOmzetten from "./pages/FormaatOmzetten/FormaatOmzetten";
import Inspiration from "./pages/Inspiration/Inspiration";
import VideoSearch from "./pages/VideoSearch/VideoSearch";
import CapacityOverview from "./pages/CapacityOverview/CapacityOverview";
import EnqueteDashboard from "./pages/EnqueteDashboard/EnqueteDashboard";
import TaskBoard from "./pages/TaskBoard/TaskBoard";
import Backlog from "./pages/Backlog/Backlog";
import PlannerTodo from "./pages/PlannerTodo/PlannerTodo";
import Inplannen from "./pages/Inplannen/Inplannen";
import Notities from "./pages/Notities/Notities";
import OneNoteImport from "./pages/Notities/OneNoteImport";
import SlimmeToewijzing from "./pages/SlimmeToewijzing/SlimmeToewijzing";
import DraaidagPlanner from "./pages/DraaidagPlanner/DraaidagPlanner";
import Vandaag from "./pages/Vandaag/Vandaag";
import MijnWerk from "./pages/MijnWerk/MijnWerk";
import ScriptBreakdown from "./pages/ScriptBreakdown/ScriptBreakdown";
import ProductieBreakdown from "./pages/ProductieBreakdown/ProductieBreakdown";
import AiVoortgang from "./pages/AiVoortgang/AiVoortgang";
import AdminSettings from "./pages/AdminSettings/AdminSettings";
import JorisDashboard from "./pages/JorisDashboard/JorisDashboard";
import SyncCheck from "./pages/SyncCheck/SyncCheck";
import AIChat from "./pages/AIChat/AIChat";
import GPTMaker from "./pages/GPTMaker/GPTMaker";
import PPTXVideoReplacer from "./pages/PPTXVideoReplacer/PPTXVideoReplacer";
import ExplainerScript from "./pages/ExplainerScript/ExplainerScript";
import AiBeelden from "./pages/AiBeelden/AiBeelden";
import ScriptstijlenPage from "./pages/Scriptstijlen/ScriptstijlenPage";
import AnimatieLeren from "./pages/Animatie/AnimatieLeren";
import AnimatieJobs from "./pages/Animatie/AnimatieJobs";
import EffieGuide from "./pages/EffieGuide/EffieGuide";
import AvatarChat from "./pages/AvatarChat/AvatarChat";
import WebsitePreview from "./pages/WebsitePreview/WebsitePreview";
import SharePointAnalysePage from "./pages/SharePointAnalysePage";
import InfoVerwerken from "./pages/InfoVerwerken/InfoVerwerken";
import MyFeedback from "./pages/MyFeedback/MyFeedback";
import Wijzigingen from "./pages/Wijzigingen/Wijzigingen";
import Bestanden from "./pages/Bestanden/Bestanden";
import SecretCodeDialog from "./ui-components/SecretCodeDialog";
import { unlockSecret } from "./utils/secretUnlock";
import MarketingOS from "./pages/MarketingOS/MarketingOS";
import VideoQAReview from "./pages/VideoQA/VideoQAReview";
import MXFCheck from "./pages/MXFCheck/MXFCheck";
import Narrowcasting from "./pages/Narrowcasting/Narrowcasting";
import OHWCheck from "./pages/OHWCheck/OHWCheck";
import UrenCheck from "./pages/UrenCheck/UrenCheck";
import SlimUren from "./pages/SlimUren/SlimUren";
import Ritmo from "./pages/Ritmo/Ritmo";
import PDFCombiner from "./pages/PDFCombiner/PDFCombiner";
import AIHub from "./pages/AIHub/AIHub";
import AccountsHub from "./pages/AccountsHub/AccountsHub";
import AccountWorkspace from "./pages/AccountsHub/AccountWorkspace";
import OudeAccountDoorverwijzing from "./pages/AccountsHub/OudeAccountDoorverwijzing";
import PlanningHub from "./pages/PlanningHub/PlanningHub";
import Corvee from "./pages/Corvee/Corvee";
import MijnProfiel from "./pages/MijnProfiel/MijnProfiel";
import OperationsHub from "./pages/OperationsHub/OperationsHub";
import ProductieHub from "./pages/ProductieHub/ProductieHub";
import MateriaalBeheer from "./pages/MateriaalBeheer/MateriaalBeheer";
import VehiclesApp from "./pages/Vehicles/VehiclesApp";
import Lab from "./pages/Lab/Lab";
import AhlstromInteractive from "./pages/Lab/AhlstromInteractive";
import Strategy from "./pages/Strategy/Strategy";
import SalesProspects from "./pages/SalesProspects/SalesProspects";
import OffertesGenerator from "./pages/Offertes/OffertesGenerator";
import Richtprijzen from "./pages/Richtprijzen/Richtprijzen";
import AchmeaExport from "./pages/AchmeaExport/AchmeaExport";
import OpdrachtMix from "./pages/OpdrachtMix/OpdrachtMix";
import MijnWeek from "./pages/Overleg/MijnWeek";
import OverlegPagina from "./pages/Overleg/OverlegPagina";
import Actieregister from "./pages/Overleg/Actieregister";
import Verbeterregister from "./pages/Verbeterregister/Verbeterregister";
import Sleuteloverzicht from "./pages/Sleutels/Sleuteloverzicht";
import WervingVacatures from "./pages/Werving/Vacatures";
import VacaturePipeline from "./pages/Werving/VacaturePipeline";
import SollicitantKaart from "./pages/Werving/SollicitantKaart";
import Budget from "./pages/Budget/Budget";
import MailAssistent from "./pages/MailAssistent/MailAssistent";
import AiMail from "./pages/AiMail/AiMail";
import { Logout } from "./pages/Logout";
import { Toaster } from 'react-hot-toast';
import Laadbalk from './components/Laadbalk';
import "./styles/App.css";
import { startTokenRefresh, stopTokenRefresh } from "./utils/tokenRefresh";
import { UserRoleProvider, useUserRole } from "./contexts/UserRoleContext";
import ErrorBoundary from "./ui-components/ErrorBoundary";
import StilInloggen from "./ui-components/StilInloggen";
import GlobalSearch from "./ui-components/GlobalSearch";
import PaginaGebruikMeter from "./ui-components/PaginaGebruikMeter";
import FeedbackFAB from "./ui-components/FeedbackFAB";
import MaintenanceBanner from "./ui-components/MaintenanceBanner";
import TestEnvironmentBanner from "./ui-components/TestEnvironmentBanner";
import RechtenFout from "./ui-components/RechtenFout";
import { AssistantProvider } from "./contexts/AssistantContext";
import { VisibilityFiltersProvider } from "./contexts/VisibilityFiltersContext";
import AssistantFAB from "./components/Assistant/AssistantFAB";
import AssistantDrawer from "./components/Assistant/AssistantDrawer";
import { isFullBleedPath, browserTitel } from "./config/navigation";

// Protected route wrapper - redirects to home if user lacks permission for feature.
// `feature` mag ook een lijst zijn: dan volstaat kijkrecht op één ervan. Dat is
// nodig voor /admin/settings, waar iemand met alleen Feedback- of Backlogrecht
// binnen moet kunnen om díé tab te zien (AdminSettings verbergt de rest).
function ProtectedRoute({ feature, children }) {
    const { permissions, loading, error, herlaad } = useUserRole();
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }
    const vereist = feature == null ? [] : [].concat(feature);
    if (vereist.length && !vereist.some((f) => permissions[f]?.view)) {
        // FE-12: rechten niet geladen ≠ geen rechten — toon dat, met een knop, i.p.v. stil naar Home.
        if (error) return <RechtenFout fout={error} opnieuw={herlaad} />;
        return <Navigate to="/" replace />;
    }
    return children;
}

function AppContent() {
    const isAuthenticated = useIsAuthenticated();
    const [searchOpen, setSearchOpen] = useState(false);
    const [secretOpen, setSecretOpen] = useState(false);
    const { instance } = useMsal();
    const activeAccount = instance.getActiveAccount();
    const userEmail = activeAccount?.username || getDevSession()?.email || "";
    const location = useLocation();
    const isFullWidthPage = isFullBleedPath(location.pathname);
    // Tabtitel per pagina: "Vandaag · Toolframe" (op test met "🧪 TEST · " ervoor).
    useEffect(() => {
        document.title = browserTitel(location.pathname, { test: isTestEnvironment });
    }, [location.pathname]);

    const handleSearchOpen = useCallback(() => setSearchOpen(true), []);
    const handleSearchClose = useCallback(() => setSearchOpen(false), []);

    useEffect(() => {
        if (isAuthenticated) {
            startTokenRefresh();
        } else {
            stopTokenRefresh();
        }
        return () => {
            stopTokenRefresh();
        };
    }, [isAuthenticated]);

    // Ctrl+K / Cmd+K shortcut
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === "k") {
                e.preventDefault();
                setSearchOpen((prev) => !prev);
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, []);

    // Geheime code: ↑↑↓↓←→←→ — geeft 2 min toegang tot geheime widgets + pop-up.
    useEffect(() => {
        const code = ['ArrowUp','ArrowUp','ArrowDown','ArrowDown','ArrowLeft','ArrowRight','ArrowLeft','ArrowRight'];
        let pos = 0;
        const handleSecret = (e) => {
            const t = e.target;
            if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
            if (e.key === code[pos]) {
                pos++;
                if (pos === code.length) {
                    pos = 0;
                    unlockSecret();
                    setSecretOpen(true);
                }
            } else {
                pos = e.key === code[0] ? 1 : 0;
            }
        };
        window.addEventListener('keydown', handleSecret);
        return () => window.removeEventListener('keydown', handleSecret);
    }, []);

    // TEST/DEV ONLY: the dev-login page is reachable without a session, and a
    // valid dev session counts as authenticated. getDevSession() returns null on
    // the production host, so both are inert on intern.fullframe.nl.
    const devSession = getDevSession();
    if (location.pathname === '/dev-login') {
        return <DevLogin />;
    }
    // Portfoliolink voor een klant: publiek, zonder inlog, zonder menu's. De
    // sleutel in het pad is de toegang (Joris, 11 sep 2026).
    // /selectie/ is het pad sinds 12 sep (de Beeldbank heeft /deel/ op zijn eigen
    // host); /deel/ blijft werken voor links die al verstuurd zijn.
    // Bestanden delen, externe ontvanger (okt 2026): persoonlijke downloadlink uit de mail.
    // Publiek, zonder inlog en zonder menu's; het token in het pad is de toegang.
    if (location.pathname.startsWith('/download/')) {
        return (
            <Routes>
                <Route path="/download/:token" element={<ExternDownload />} />
            </Routes>
        );
    }
    if (location.pathname.startsWith('/deel/') || location.pathname.startsWith('/selectie/')) {
        return (
            <Routes>
                <Route path="/deel/:token" element={<PortfolioPagina />} />
                <Route path="/selectie/:token" element={<PortfolioPagina />} />
            </Routes>
        );
    }
    if (!isAuthenticated && !devSession) {
        return <Home />;
    }

    return (
        <AppShell onSearchClick={handleSearchOpen}>
            {isFullWidthPage ? (
                <Pages />
            ) : (
                <Grid container justifyContent="center" sx={{ width: '100%' }}>
                    <Pages />
                </Grid>
            )}
            <Toaster position="bottom-right" />
            {/* Eén laadindicator voor de hele app (4 nov): elk lopend verzoek, ook op de achtergrond van een scherm. */}
            <Laadbalk />
            {/* Paginagebruik: één beacon per routewissel, alleen route-sjabloon (26 sep) */}
            <PaginaGebruikMeter />
            <GlobalSearch
                open={searchOpen}
                onClose={handleSearchClose}
                userEmail={userEmail}
            />
            {!location.pathname.startsWith('/lab/') && <FeedbackFAB />}
            {!location.pathname.startsWith('/lab/') && <AssistantFAB />}
            <AssistantDrawer />
            <SecretCodeDialog open={secretOpen} onClose={() => setSecretOpen(false)} />
        </AppShell>
    );
}

function Pages() {
    return (
        <Routes>
            <Route path="/logout" element={<Logout />} />
            {/* "/" = Vandaag (startplek, ronde 4). Het widgetbord staat onder /home. */}
            <Route path="/" element={<Navigate to="/vandaag" replace />} />
            <Route path="/home" element={<Home />} />
            <Route path="/beta" element={<Home full />} />
            <Route path="MaterialReservation" element={<MaterialReservation />} />
            <Route path="ondertiteling" element={<Ondertiteling />} />
            <Route path="SRTGenerator" element={<SRTGenerator />} />
            <Route path="ritmo" element={<Ritmo />} />
            <Route path="SRTDashboard" element={<SRTDashboard />} />
            <Route path="BatchTranslate" element={<BatchTranslate />} />
            <Route path="BatchTranscribe" element={<BatchTranscribe />} />
            <Route path="SRTomzettenFCP" element={<SRTomzettenFCP/>}/>
            <Route path="MeetingDashboard" element={<MeetingDashboard />} />
            <Route path="MeetingDashboard/:id" element={<MeetingDetailPage />} />
            <Route path="overleg" element={<ProtectedRoute feature="overleg"><MijnWeek /></ProtectedRoute>} />
            <Route path="overleg/acties" element={<ProtectedRoute feature="overleg"><Actieregister /></ProtectedRoute>} />
            <Route path="verbeterregister" element={<ProtectedRoute feature="overleg"><Verbeterregister /></ProtectedRoute>} />
            <Route path="overleg/sessie/:id" element={<ProtectedRoute feature="overleg"><OverlegPagina /></ProtectedRoute>} />
            <Route path="dashboard" element={<RoleDashboard />} />
            <Route path="dagstart" element={<Dagstart />} />
            <Route path="draaidagen" element={<DraaidagenOverzicht />} />
            <Route path="planning/overzicht-per-project" element={<OverzichtPerProject />} />
            <Route path="planning/weekoverzicht-pl" element={<WeekoverzichtPL />} />
            {/* "Mijn Week" heet nu Dagstart; bestaande bladwijzers en links blijven werken. */}
            <Route path="week-overview" element={<Navigate to="/dagstart" replace />} />
            <Route path="weekplan" element={<WeekPlan />} />
            <Route path="mijn-taken" element={<MijnTaken />} />
            <Route path="mijn-todos" element={<MijnTodos />} />
            <Route path="aanvragen-portaal" element={<AanvragenPortaal />} />
            <Route path="contentkalender" element={<Contentkalender />} />
            <Route path="overdracht" element={<Overdracht />} />
            <Route path="leveranciersbeoordelingen" element={<LeveranciersBeoordelingen />} />
            {/* HR & leveranciers (26 sep): galerij voor iedereen, dashboard en leveranciers achter hr_leveranciers */}
            <Route path="medewerkers" element={<Medewerkers />} />
            <Route path="hr-dashboard" element={<ProtectedRoute feature="hr_leveranciers"><HrDashboard /></ProtectedRoute>} />
            <Route path="leveranciers" element={<ProtectedRoute feature={["leveranciers", "hr_leveranciers"]}><Leveranciers /></ProtectedRoute>} />
            <Route path="sales-dashboard" element={<ProtectedRoute feature="sales_dashboard"><SalesDashboard /></ProtectedRoute>} />
            {/* Oude losse klantpagina (26 sep opgeruimd): bladwijzers landen op de accountpagina, tab Cijfers. Zelfde Airtable-account-id. */}
            <Route path="account/:accountId" element={<OudeAccountDoorverwijzing />} />
            <Route path="revenue-forecast" element={<ProtectedRoute feature="revenue_forecast"><RevenueForecast /></ProtectedRoute>} />
            <Route path="bbi-doel" element={<ProtectedRoute feature="kpi_dashboard"><BbiDoel /></ProtectedRoute>} />
            <Route path="declarabiliteit" element={<ProtectedRoute feature="kpi_dashboard"><Declarabiliteit /></ProtectedRoute>} />
            <Route path="declarabiliteit/persoon/:email" element={<ProtectedRoute feature="kpi_dashboard"><DeclarabiliteitPersoon /></ProtectedRoute>} />
            <Route path="video-downloader" element={<VideoDownloader />} />
            <Route path="color-converter" element={<ColorConverter />} />
            <Route path="qr-generator" element={<QRGenerator />} />
            <Route path="name-title-generator" element={<NameTitleGenerator />} />
            <Route path="formaat-omzetten" element={<FormaatOmzetten />} />
            <Route path="inspiration" element={<Inspiration />} />
            <Route path="video-search" element={<VideoSearch />} />
            <Route path="video-qa" element={<VideoQAReview />} />
            <Route path="mxf-check" element={<ProtectedRoute feature="mxf_check"><MXFCheck /></ProtectedRoute>} />
            <Route path="capacity" element={<CapacityOverview />} />
            <Route path="enquete-dashboard" element={<ProtectedRoute feature="enquete_dashboard"><EnqueteDashboard /></ProtectedRoute>} />
            <Route path="sleutels" element={<ProtectedRoute feature="sleutels"><Sleuteloverzicht /></ProtectedRoute>} />
            <Route path="werving" element={<ProtectedRoute feature="werving"><WervingVacatures /></ProtectedRoute>} />
            <Route path="werving/vacature/:id" element={<ProtectedRoute feature="werving"><VacaturePipeline /></ProtectedRoute>} />
            <Route path="werving/sollicitatie/:id" element={<ProtectedRoute feature="werving"><SollicitantKaart /></ProtectedRoute>} />
            <Route path="budget" element={<ProtectedRoute feature="budget"><Budget /></ProtectedRoute>} />
            <Route path="projects" element={<ProjectList />} />
            <Route path="projects/:id" element={<ProjectOverview />} />
            <Route path="klanten" element={<Navigate to="/accounts" replace />} />
            <Route path="callsheets" element={<CallSheetList />} />
            <Route path="callsheets/:id" element={<CallSheetManager />} />
            <Route path="callsheets/:id/briefing" element={<CallSheetBriefing />} />
            <Route path="vandaag" element={<Vandaag />} />
            <Route path="mijn-werk" element={<MijnWerk />} />
            <Route path="task-board" element={<TaskBoard />} />
            {/* Het ontwikkelbord is een werkbord voor de bouw, geen bedrijfsdata.
                Alleen op test: op productie bestaan de tabellen niet eens en geeft
                de API een 404, dus de route hoort daar ook niet te bestaan. */}
            {isTestEnvironment && (
              <Route path="backlog" element={<ProtectedRoute feature="backlog"><Backlog /></ProtectedRoute>} />
            )}
            <Route path="planner-todo" element={<ProtectedRoute feature="planner_todo"><PlannerTodo /></ProtectedRoute>} />
            <Route path="planning/inplannen" element={<ProtectedRoute feature="planner_todo"><Inplannen /></ProtectedRoute>} />
            <Route path="notes" element={<Notities />} />
            <Route path="notes/onenote-import" element={<OneNoteImport />} />
            <Route path="slimme-toewijzing" element={<ProtectedRoute feature="planner_todo"><SlimmeToewijzing /></ProtectedRoute>} />
            <Route path="draaidag-planner" element={<ProtectedRoute feature="planner_todo"><DraaidagPlanner /></ProtectedRoute>} />
            <Route path="tasks/new" element={<CreateTask />} />
            <Route path="deliverables/new" element={<CreateDeliverable />} />
            <Route path="archive/view/:id" element={<ArchiveViewer />} />
            <Route path="script-breakdown" element={<ScriptBreakdown />} />
            <Route path="productie-breakdown" element={<ProtectedRoute feature="productie_breakdown"><ProductieBreakdown /></ProtectedRoute>} />
            <Route path="productie-breakdown/:productieId" element={<ProtectedRoute feature="productie_breakdown"><ProductieBreakdown /></ProtectedRoute>} />
            <Route path="ai-voortgang" element={<ProtectedRoute feature="ai_voortgang"><AiVoortgang /></ProtectedRoute>} />
            <Route path="ai-voortgang/:projectId" element={<ProtectedRoute feature="ai_voortgang"><AiVoortgang /></ProtectedRoute>} />
            <Route path="ai-chat" element={<AIChat />} />
            <Route path="gpt-maker" element={<GPTMaker />} />
            <Route path="pptx-video-replacer" element={<PPTXVideoReplacer />} />
            <Route path="explainer-script" element={<ExplainerScript />} />
            <Route path="ai-beelden" element={<AiBeelden />} />
            <Route path="scriptstijlen" element={<ScriptstijlenPage />} />
            <Route path="animatie-leren" element={<AnimatieLeren />} />
            <Route path="animatie-jobs" element={<AnimatieJobs />} />
            <Route path="effie-guide" element={<EffieGuide />} />
            <Route path="avatar-chat" element={<AvatarChat />} />
            <Route path="admin/settings" element={<ProtectedRoute feature={["admin_settings", "feedback_beheer", "backlog", "werving"]}><AdminSettings /></ProtectedRoute>} />
            <Route path="joris-dashboard" element={<ProtectedRoute feature="kpi_dashboard"><JorisDashboard /></ProtectedRoute>} />
            <Route path="sync-check" element={<SyncCheck />} />
            <Route path="lab" element={<ProtectedRoute feature="lab"><Lab /></ProtectedRoute>} />
            <Route path="lab/ahlstrom-interactive" element={<ProtectedRoute feature="lab"><AhlstromInteractive /></ProtectedRoute>} />
            <Route path="strategy" element={<ProtectedRoute feature="strategy"><Strategy /></ProtectedRoute>} />
            <Route path="sales/prospects" element={<ProtectedRoute feature="sales_prospects"><SalesProspects /></ProtectedRoute>} />
            <Route path="prospects" element={<Navigate to="/sales/prospects" replace />} />
            <Route path="offertes" element={<ProtectedRoute feature="offertes"><OffertesGenerator /></ProtectedRoute>} />
            <Route path="richtprijzen" element={<ProtectedRoute feature="offertes"><Richtprijzen /></ProtectedRoute>} />
            <Route path="achmea-export" element={<ProtectedRoute feature="offertes"><AchmeaExport /></ProtectedRoute>} />
            <Route path="opdracht-mix" element={<ProtectedRoute feature="kpi_dashboard"><OpdrachtMix /></ProtectedRoute>} />
            <Route path="mail-assistent" element={<MailAssistent />} />
            <Route path="ai-mail" element={<AiMail />} />
            <Route path="sales/offertes" element={<Navigate to="/offertes" replace />} />
            <Route path="my-feedback" element={<MyFeedback />} />
            <Route path="wijzigingen" element={<ProtectedRoute feature="wijzigingen"><Wijzigingen /></ProtectedRoute>} />
            <Route path="bestanden" element={<ProtectedRoute feature="bestanden"><Bestanden /></ProtectedRoute>} />
            <Route path="narrowcasting" element={<Narrowcasting />} />
            <Route path="ai-hub" element={<AIHub />} />
            <Route path="planning" element={<PlanningHub />} />
            <Route path="planning/corvee/*" element={<Corvee />} />
            <Route path="mijn-profiel" element={<MijnProfiel />} />
            <Route path="productie" element={<ProductieHub />} />
            <Route path="materiaal-beheer" element={<Navigate to="/materiaal-beheer/materialen" replace />} />
            <Route path="materiaal-beheer/:tab" element={<MateriaalBeheer />} />
            <Route path="planning/voertuigen" element={<VehiclesApp />} />
            <Route path="operations" element={<OperationsHub />} />
            <Route path="accounts" element={<AccountsHub />} />
            <Route path="accounts/:accountId/workspace" element={<AccountWorkspace />} />
            <Route path="pdf-combiner" element={<PDFCombiner />} />
            <Route path="ohw-check" element={<ProtectedRoute feature="kpi_dashboard"><OHWCheck /></ProtectedRoute>} />
            <Route path="uren-check" element={<ProtectedRoute feature="kpi_dashboard"><UrenCheck /></ProtectedRoute>} />
            <Route path="slim-uren" element={<SlimUren />} />
            <Route path="marketing" element={<MarketingOS />} />
            <Route path="website-preview" element={<ProtectedRoute feature="website_feedback"><WebsitePreview /></ProtectedRoute>} />
            <Route path="sharepoint-analyse" element={<ProtectedRoute feature="sharepoint_analyse"><SharePointAnalysePage /></ProtectedRoute>} />
            {/* info@-samenvatting → "Verwerken zoals aangegeven" (link uit de mail; bevestigen hier, nooit vanuit de mail) */}
            <Route path="algemene-mailboxen/info/verwerken" element={<ProtectedRoute feature={["admin_settings", "algemene_mailboxen"]}><InfoVerwerken /></ProtectedRoute>} />
        </Routes>
    );
}

export default function App({ pca }) {
    // Configure MSAL to use router's navigate functions
    const navigate = useNavigate();
    const navigationClient = new CustomNavigationClient(navigate);
    pca.setNavigationClient(navigationClient);
    
    return (
        <>
            <TestEnvironmentBanner />
            <MaintenanceBanner />
            <MsalProvider instance={pca}>
                <ErrorBoundary>
                  <StilInloggen>
                    <UserRoleProvider>
                        <VisibilityFiltersProvider>
                            <AssistantProvider>
                                <AppContent />
                            </AssistantProvider>
                        </VisibilityFiltersProvider>
                    </UserRoleProvider>
                  </StilInloggen>
                </ErrorBoundary>
            </MsalProvider>
        </>
    );
}
